/* global amounts, listQuantityItems, imgUrl, assignedToEvent, errors, expenseReadonly, isTemplate */

import React, { Fragment, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import CreatableSelect from 'react-select/creatable';

import NumberFormat from 'CommonComponents/LocalisedNumberFormat';

export default function ExpenseQuantityEdit(props) {
    const [selectedAmounts, setSelectedAmounts] = useState(amounts);
    const [list, setList] = useState(listQuantityItems);
    const [availableLists, setAvailableLists] = useState([]);
    // Quantity links chosen before the expense exists, keyed by slot index.
    // Submitted as hidden quantity_link[…] inputs and applied after creation.
    const [deferredLinks, setDeferredLinks] = useState({});
    // Remembers which slot opened the picker (quantitylink:selected carries only the source).
    const pendingSlotRef = useRef(null);

    const linkEnabled = typeof window.quantityLinkEnabled !== 'undefined' && window.quantityLinkEnabled;
    const createMode = typeof window.quantityLinkCreateMode !== 'undefined' && window.quantityLinkCreateMode;

    // The rate card item chosen on the name field above, or null for a manual
    // line. The picker is a separate React root, so it arrives by window event —
    // the same idiom the quantity-link picker already uses.
    //
    // Its `unit` may legitimately be empty: an item with no unit fixes nothing,
    // so the slot stays editable even though the single-quantity rule still
    // applies. `rateCardSelection !== null` is therefore the wrong test for a
    // locked unit and the right one for hiding "Add another quantity".
    const [rateCardSelection, setRateCardSelection] = useState(null);

    // What the quantity slots held before a rate card item collapsed them to
    // one, so ✕ or Esc gives them back instead of leaving them to be rebuilt.
    const amountsBeforeRateCardRef = useRef(null);

    const unitLocked = Boolean(rateCardSelection && rateCardSelection.unit);

    const openDeferredPicker = (slotIndex, current) => {
        pendingSlotRef.current = slotIndex;
        window.dispatchEvent(
            new CustomEvent('quantitylink:open', {
                detail: {
                    deferred: true,
                    sourcesUrl: window.quantityLinkCreateSourcesUrl,
                    amountSlotIndex: slotIndex,
                    currentLink: current,
                },
            }),
        );
    };

    const clearDeferredLink = (slotIndex) => {
        setDeferredLinks((prev) => {
            const next = { ...prev };
            delete next[slotIndex];
            return next;
        });
    };

    useEffect(() => {
        const handleSelected = (e) => {
            const slotIndex = pendingSlotRef.current;
            pendingSlotRef.current = null;
            if (slotIndex === null || slotIndex === undefined) return;
            const sel = e.detail || {};
            setDeferredLinks((prev) => ({
                ...prev,
                [slotIndex]: { sourceType: sel.sourceType, sourceId: sel.sourceId, driver: sel.driver ?? null, label: sel.label, value: sel.value },
            }));
            // Reflect the live source value into the quantity slot immediately.
            setSelectedAmounts((prev) => {
                const data = JSON.parse(JSON.stringify(prev));
                if (data[slotIndex]) {
                    data[slotIndex].amount = sel.value ?? 0;
                }
                return data;
            });
        };
        window.addEventListener('quantitylink:selected', handleSelected);
        return () => window.removeEventListener('quantitylink:selected', handleSelected);
    }, []);

    // A rate card line is priced per its item's unit and carries exactly one
    // quantity: two units or two rates are two lines, so the slot's unit is
    // fixed and "Add another quantity" has nothing to add.
    useEffect(() => {
        const handleRateCardSelected = (e) => {
            const unit = e.detail?.unit || '';
            setRateCardSelection({ unit });
            setSelectedAmounts((prev) => {
                // Only the first selection snapshots: switching straight from one
                // item to another must still restore the slots the person built,
                // not the single slot the previous item left behind.
                if (amountsBeforeRateCardRef.current === null) {
                    amountsBeforeRateCardRef.current = JSON.parse(JSON.stringify(prev));
                }

                const data = JSON.parse(JSON.stringify(prev));
                const first = data[0] || { amount: 1 };
                first.name = unit || first.name;
                return [first];
            });
        };

        const handleRateCardCleared = () => {
            setRateCardSelection(null);
            setSelectedAmounts((prev) => {
                const restored = amountsBeforeRateCardRef.current;
                amountsBeforeRateCardRef.current = null;

                if (!restored) return prev;

                // The quantity itself is the person's own — the picker only ever
                // replaced the unit and dropped the extra slots — so slot 0 keeps
                // whatever it currently holds.
                const next = JSON.parse(JSON.stringify(restored));
                if (next[0] && prev[0]) next[0].amount = prev[0].amount;

                return next;
            });
        };

        window.addEventListener('ratecard:selected', handleRateCardSelected);
        window.addEventListener('ratecard:cleared', handleRateCardCleared);

        return () => {
            window.removeEventListener('ratecard:selected', handleRateCardSelected);
            window.removeEventListener('ratecard:cleared', handleRateCardCleared);
        };
    }, []);

    useEffect(() => {
        const avLists = JSON.parse(JSON.stringify(availableLists));
        avLists[0] = availableList(0);
        setAvailableLists[avLists];

        if (selectedAmounts.length === 0) {
            const res = [];
            res.push({
                name: avLists[0],
                amount: 1,
            });

            setSelectedAmounts(res);
        }
    }, []);

    useEffect(() => {
        const avLists = JSON.parse(JSON.stringify(availableLists));
        selectedAmounts.forEach((item, index) => {
            avLists[index] = availableList(index);
        });
        setAvailableLists[avLists];
    }, [list, selectedAmounts]);

    const availableList = (index) => {
        if (index === 0) {
            return list;
        }

        const excludedNames = [];
        selectedAmounts.forEach((item, i) => {
            if (i < index) {
                excludedNames.push(item.name);
            }
        });

        return list.filter((name) => {
            return !excludedNames.includes(name);
        });
    };

    const changeAmountValue = (index, key, value) => {
        const allList = JSON.parse(JSON.stringify(list));

        if (key === 'name' && !allList.includes(value)) {
            allList.push(value);
            setList(allList);
        }
        const data = JSON.parse(JSON.stringify(selectedAmounts));
        const selectedData = selectedAmountNames();

        if (selectedData.includes(value) && key === 'name') {
            /* empty */
        } else {
            data[index][key] = value;
            setSelectedAmounts(data);
        }
    };

    const selectedAmountNames = () => {
        const res = [];
        selectedAmounts.forEach((item) => {
            res.push(item.name);
        });

        return res;
    };

    const unitsObj = (index) => {
        const res = [];
        availableList(index).forEach((item, index) => {
            res.push({ label: item, value: item });
        });

        return res;
    };

    const addQuantity = (e) => {
        e.preventDefault();

        if (selectedAmounts.length >= 3) {
            return false;
        }

        const ind = selectedAmounts.length;
        const res = JSON.parse(JSON.stringify(selectedAmounts));

        res.push({
            name: availableList(ind)[0],
            amount: 1,
        });

        setSelectedAmounts(res);
    };

    const removeQuantity = (e, index) => {
        e.preventDefault();
        let res = JSON.parse(JSON.stringify(selectedAmounts));
        res.splice(index, 1);

        setSelectedAmounts(res);
    };

    return (
        <div className="form-group row" id="edit-quantity">
            <label className="col-sm-3 col-form-label tw-text-base">Quantity:</label>
            <div className="tmpl-holder col-sm-9" id="multiple-amount">
                {selectedAmounts.length > 0 && (
                    <>
                        {selectedAmounts.map((expAmount, index) => {
                            return (
                                <ExpenseQuantityEditRow
                                    key={`$quantity-${index}`}
                                    helper={false}
                                    index={index}
                                    name={expAmount.name}
                                    amount={expAmount.amount}
                                    imageUrl={imgUrl}
                                    assignedToEvent={assignedToEvent}
                                    errors={errors}
                                    units={availableLists[index]}
                                    unitsObj={unitsObj(index)}
                                    changeAmountValue={changeAmountValue}
                                    unitLocked={unitLocked}
                                    expenseReadonly={expenseReadonly}
                                    list={list}
                                    removeQuantity={removeQuantity}
                                    linkEnabled={linkEnabled}
                                    createMode={createMode}
                                    deferredLink={deferredLinks[index]}
                                    openDeferredPicker={openDeferredPicker}
                                    clearDeferredLink={clearDeferredLink}
                                />
                            );
                        })}
                    </>
                )}
                {selectedAmounts.length === 0 && (
                    <>
                        <ExpenseQuantityEditRow
                            helper={false}
                            index={0}
                            imageUrl={imgUrl}
                            assignedToEvent={assignedToEvent}
                            errors={errors}
                            units={availableLists[0]}
                            unitsObj={unitsObj(0)}
                            changeAmountValue={changeAmountValue}
                            unitLocked={unitLocked}
                            list={list}
                            removeQuantity={removeQuantity}
                            linkEnabled={linkEnabled}
                            createMode={createMode}
                            deferredLink={deferredLinks[0]}
                            openDeferredPicker={openDeferredPicker}
                            clearDeferredLink={clearDeferredLink}
                        />
                    </>
                )}
                {Object.keys(deferredLinks).map((slotIndex) => {
                    const link = deferredLinks[slotIndex];
                    return (
                        <Fragment key={`ql-${slotIndex}`}>
                            <input type="hidden" name={`quantity_link[${slotIndex}][source_type]`} value={link.sourceType ?? ''} />
                            <input type="hidden" name={`quantity_link[${slotIndex}][source_id]`} value={link.sourceId ?? ''} />
                            <input type="hidden" name={`quantity_link[${slotIndex}][amount_slot_index]`} value={slotIndex} />
                            {link.driver && <input type="hidden" name={`quantity_link[${slotIndex}][driver]`} value={link.driver} />}
                        </Fragment>
                    );
                })}
                {unitLocked && <div className="tw-mt-1 tw-text-2xs tw-text-charcoal-50">{$t('Unit fixed by the rate card item')}</div>}
                {rateCardSelection === null && !isTemplate && !expenseReadonly && selectedAmounts.length < 3 && (
                    <button className="button primary" onClick={addQuantity}>
                        {$t('Add another quantity')}
                    </button>
                )}
            </div>
        </div>
    );
}

if (document.getElementById('expenseQuantity')) {
    var root = document.getElementById('expenseQuantity');
    ReactDOM.render(
        <App>
            <ExpenseQuantityEdit />
        </App>,
        root,
    );
}

function ExpenseQuantityEditRow(props) {
    const changeAmount = (values) => {
        props.changeAmountValue(props.index, 'amount', values.value ?? '');
    };

    const changeName = (e) => {
        const val = e.value;
        props.changeAmountValue(props.index, 'name', val);
    };

    const linkEnabled = props.linkEnabled ?? (typeof window.quantityLinkEnabled !== 'undefined' && window.quantityLinkEnabled);
    const createMode = props.createMode ?? (typeof window.quantityLinkCreateMode !== 'undefined' && window.quantityLinkCreateMode);
    const deferredLink = props.deferredLink ?? null;
    const isLinked = linkEnabled && createMode && !!deferredLink;
    const sourceLabel = deferredLink?.label || '';

    const handleOpenPicker = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (createMode) {
            // Capture the source up front; the link is applied server-side after
            // the expense is created (mirrors the quick-add flow).
            props.openDeferredPicker?.(
                props.index,
                deferredLink ? { sourceType: deferredLink.sourceType, sourceId: deferredLink.sourceId, driver: deferredLink.driver ?? null } : null,
            );
            return;
        }
        window.dispatchEvent(
            new CustomEvent('quantitylink:open', {
                detail: {
                    expenseId: window.quantityLinkExpenseId,
                    sourcesUrl: window.quantityLinkSourcesUrl,
                    linkUrl: window.quantityLinkLinkUrl,
                    unlinkUrl: window.quantityLinkUnlinkUrl,
                    amountSlotIndex: props.index,
                    currentLink: null,
                },
            }),
        );
    };

    const handleUnlink = (e) => {
        e.preventDefault();
        e.stopPropagation();
        props.clearDeferredLink?.(props.index);
    };

    // prettier-ignore
    const linkSvg = (
        <svg className="tw-w-4 tw-h-4 tw-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
        </svg>
    );

    return (
        <div className={`row mb-2 tmpl tmpl-item ${props.helper ? 'd-none' : ' multiple-dates'}`} data-tmpl-name="amounts" data-index={props.index || '0'}>
            <div className="col">
                <div className="tw-flex tw-w-full max-lg:tw-flex-col tw-border-0 tw-rounded-lg tw-bg-white tw-shadow tw-gap-x-1 max-lg:tw-gap-y-1 tw-items-center tw-p-2">
                    <a className={'tw-w-1/12 max-lg:tw-place-self-start'}>
                        <img className={'tw-flex tw-items-center'} src={props.imageUrl} />
                    </a>
                    {!!props.assignedToEvent && (
                        <div className="tw-w-5/12 max-lg:tw-w-full">
                            <div className="tw-flex tw-items-center tw-gap-1">
                                <div className="tw-relative tw-flex-1">
                                    <NumberFormat
                                        className={`tw-w-full tw-rounded-lg tw-bg-input-background tw-text-input-text tw-text-xs tw-font-semibold ${
                                            props.errors && props.errors.amount && props.errors.amount[props.index] && props.errors.amount[props.index].amount
                                                ? 'is-invalid'
                                                : ''
                                        } ${isLinked ? '!tw-bg-blue-15 !tw-border !tw-border-blue' : ''} ${linkEnabled ? 'tw-pr-10' : ''}`}
                                        data-name={props.helper ? `"amount[${props.index}][amount]"` : ''}
                                        disabled={!!props.expenseReadonly}
                                        // Linked slots are read-only (not disabled) so the
                                        // source-driven value still renders; the value is
                                        // submitted via the hidden input below.
                                        readOnly={isLinked}
                                        value={props.name ? props.amount : ''}
                                        thousandSeparator
                                        allowNegative={false}
                                        inputMode="decimal"
                                        onValueChange={changeAmount}
                                    />
                                    {linkEnabled && (
                                        <button
                                            type="button"
                                            className="tw-absolute tw-right-1 tw-top-1/2 -tw-translate-y-1/2 tw-inline-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-lg tw-bg-blue tw-text-white hover:tw-bg-blue-75 tw-transition-colors tw-shrink-0"
                                            title={$t('Link quantity to an embedded source')}
                                            aria-label={$t('Link quantity to an embedded source')}
                                            onClick={handleOpenPicker}
                                        >
                                            {linkSvg}
                                        </button>
                                    )}
                                </div>
                                {isLinked && (
                                    <button
                                        type="button"
                                        className="tw-inline-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-lg tw-text-red-500 hover:tw-text-white hover:tw-bg-red-500 tw-transition-colors tw-shrink-0"
                                        title={$t('Unlink quantity from source. The current value will be frozen.')}
                                        aria-label={$t('Unlink quantity from source')}
                                        onClick={handleUnlink}
                                    >
                                        {/* prettier-ignore */}
                                        <svg className="tw-w-4 tw-h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                                    </button>
                                )}
                            </div>
                            {isLinked && (
                                <span className="tw-flex tw-items-baseline tw-gap-1.5 tw-mt-1" title={sourceLabel}>
                                    <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-blue tw-shrink-0 tw-relative tw-top-px" />
                                    <span className="tw-text-2xs tw-text-blue tw-break-words tw-min-w-0">
                                        {$t('Linked to')} <span className="tw-underline">{sourceLabel}</span>
                                    </span>
                                </span>
                            )}
                            {!props.helper && <input type="hidden" name={`amount[${props.index}][amount]`} value={props.amount ?? ''} />}
                        </div>
                    )}
                    {!!props.expenseReadonly && <div className={'tw-w-1/12 max-lg:tw-hidden'}></div>}
                    {!props.assignedToEvent && <input type="hidden" name={`amount[${props.index}][amount]`} value="1" />}
                    <div className="tw-w-5/12 max-lg:tw-w-full">
                        {props.unitLocked ? (
                            // NOT a disabled react-select: it renders no hidden input at
                            // all, so `amount[index][name]` would silently vanish from the
                            // form post and the unit would come back blank on save, with
                            // nothing on screen to suggest why. The locked state is a
                            // read-only field carrying its own hidden input instead.
                            <>
                                <input
                                    type="text"
                                    readOnly
                                    className="tw-w-full tw-rounded-lg tw-bg-charcoal-10 tw-text-charcoal-75 tw-text-xs tw-font-semibold tw-border-0"
                                    value={props.name ? `${props.name} ${$t('(locked)')}` : $t('(locked)')}
                                    aria-label={$t('Unit fixed by the rate card item')}
                                />
                                <input type="hidden" name={`amount[${props.index}][name]`} value={props.name ?? ''} />
                            </>
                        ) : (
                            <CreatableSelect
                                options={props.unitsObj}
                                disabled={!!props.expenseReadonly}
                                onChange={changeName}
                                value={{ label: props.name, value: props.name }}
                                name={`amount[${props.index}][name]`}
                                className="react-select-container"
                                classNamePrefix="react-select"
                            />
                        )}
                    </div>
                    {!props.expenseReadonly && !props.unitLocked && (
                        <div className="tw-w-1/12">
                            <button
                                className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-text-xs"
                                onClick={(e) => props.removeQuantity(e, props.index)}
                            >
                                <i className="far fa-trash-alt h5 mb-0"></i>
                                <small className="font-weight-600 w-100 d-block">{$t('Delete')}</small>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
